import { adminRequest, type AdminResult } from "./adminApi";

/** The kinds of unit a budget can sit on: a Division, a Department or an entitlement group (`BudgetUnitResponse.Kind`). */
export type BudgetUnitKind = "Division" | "Department" | "Group";

/**
 * One Division, Department or group as the budget tree shows it at the current instant, from `GET /api/entitlement/budgets`
 * (`Rws.AiInsights.Host.Administration.BudgetUnitResponse`). The budget is the explicit amount when there is one, otherwise the
 * implied amount; the hard stop source is the unit whose setting applies to this one, which may be an ancestor.
 */
export interface BudgetUnit {
  kind: BudgetUnitKind;
  id: string;
  name: string;
  parentKind: BudgetUnitKind | null;
  parentId: string | null;
  /** The unit's own monthly budget in whole USD, or null when it has none. */
  explicitBudgetUsd: number | null;
  /** The sum of the budgets beneath the unit, or null when nothing beneath has one. */
  impliedBudgetUsd: number | null;
  /** The explicit budget when there is one, otherwise the implied one; null when there is no budget at all (not $0). */
  budgetUsd: number | null;
  /** What the children take up of this unit's explicit budget. */
  allocatedUsd: number;
  /** Whether the unit's own hard-stop setting is on. */
  ownHardStop: boolean;
  /** Whether the hard stop applies to the unit, from itself or an ancestor. */
  effectiveHardStop: boolean;
  hardStopSourceKind: BudgetUnitKind | null;
  hardStopSourceId: string | null;
  /** The name of the unit whose hard stop applies, shown as the source of an inherited setting. */
  hardStopSourceName: string | null;
  /** Whether the unit itself is marked as Capex. */
  ownCapex: boolean;
  /** Whether the unit's spend is Capex, because the unit or an ancestor is marked. Capex is inherited down the tree. */
  effectiveCapex: boolean;
  capexSourceKind: BudgetUnitKind | null;
  capexSourceId: string | null;
  /** The name of the unit whose Capex mark applies, shown as the source of an inherited mark. */
  capexSourceName: string | null;
}

/** The body of `PUT /api/entitlement/budgets/{kind}/{id}` (`SetBudgetRequest`). */
export interface SetBudgetRequest {
  /** The monthly budget in whole USD, or null to clear the explicit budget. */
  amountUsd: number | null;
  hardStop: boolean;
  /** `YYYY-MM-DD` from which the change applies; needs `budgets.backdate` when set. */
  effectiveFrom: string | null;
  /** The unit's own Capex mark; leave it out (or null) to keep the one in effect. A unit beneath a Capex unit may not change it. */
  capex?: boolean | null;
}

/** One budget record of a unit (`BudgetHistoryResponse`), newest first. A record with no `effectiveTo` is the one in effect. */
export interface BudgetHistoryRecord {
  amountUsd: number | null;
  hardStop: boolean;
  capex: boolean;
  /** An ISO timestamp, at the day the record starts to apply. */
  effectiveFrom: string;
  effectiveTo: string | null;
  setByPersonId: string | null;
  setByName: string | null;
  setAt: string;
  backdated: boolean;
}

/** One allowance record of a group (`GroupAllowanceHistoryResponse`). `effectiveFrom` null means it applied since records began. */
export interface GroupAllowanceHistoryRecord {
  amountUsd: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  setByPersonId: string | null;
  setAt: string;
}

const budgetsPath = "/api/entitlement/budgets";
const groupsPath = "/api/entitlement/groups";

/** Calls `GET /api/entitlement/budgets`: every Division, Department and group with its budget and hard stop. */
export async function fetchEntitlementBudgets(): Promise<AdminResult<BudgetUnit[]>> {
  return adminRequest<BudgetUnit[]>(budgetsPath, { method: "GET" }, "budgets");
}

/**
 * Calls `PUT /api/entitlement/budgets/{kind}/{id}`. A flow-down breach comes back as an `error` whose message is the server's
 * detail, naming the ancestor and the excess; a back-dated change without `budgets.backdate` comes back as `denied`.
 */
export async function saveEntitlementBudget(kind: BudgetUnitKind, id: string, request: SetBudgetRequest): Promise<AdminResult<BudgetUnit>> {
  return adminRequest<BudgetUnit>(`${budgetsPath}/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(request) }, "budget");
}

/** Calls `GET /api/entitlement/budgets/{kind}/{id}/history`. */
export async function fetchEntitlementBudgetHistory(kind: BudgetUnitKind, id: string): Promise<AdminResult<BudgetHistoryRecord[]>> {
  return adminRequest<BudgetHistoryRecord[]>(`${budgetsPath}/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/history`, { method: "GET" }, "budget history");
}

/** Calls `GET /api/entitlement/groups/{groupId}/allowance-history`. */
export async function fetchEntitlementGroupAllowanceHistory(groupId: string): Promise<AdminResult<GroupAllowanceHistoryRecord[]>> {
  return adminRequest<GroupAllowanceHistoryRecord[]>(`${groupsPath}/${encodeURIComponent(groupId)}/allowance-history`, { method: "GET" }, "allowance history");
}
